import { useState } from 'react'

import {
  DraftPerformanceReviewEvaluationAnswerDocument,
  PerformanceAnswer,
  PerformanceAnswerInput,
  PerformanceQuestionnaireAnsweredElement,
  PublishPerformanceReviewEvaluationAnswerDocument,
  RepublishPerformanceReviewEvaluationAnswerDocument,
  ReviewAnswerEvaluationFragment,
} from 'generated/resources/graphql'
import { remove } from 'lodash'
import { useDebounce } from 'usehooks-ts'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

const anyQuestionAnswered = (answers: PerformanceAnswer[]) =>
  answers.some(
    (answer) =>
      answer.answerText !== null ||
      answer.answerRating !== null ||
      answer.answerChoice !== null ||
      answer.answerInt !== null
  )

const prepareAnswerInput = (answer: PerformanceAnswer): PerformanceAnswerInput | undefined => {
  if (answer.answerRating && answer.answerRating.value != null) {
    return {
      uuid: answer.uuid,
      answerRating: {
        value: answer.answerRating.value,
        comment: answer.answerRating.comment || null,
      },
    }
  }
  if (answer.answerText && answer.answerText != '') {
    return {
      uuid: answer.uuid,
      answerText: answer.answerText,
    }
  }
  if (answer.answerInt != null) {
    return {
      uuid: answer.uuid,
      answerInt: answer.answerInt,
    }
  }
  if (!!answer.answerChoice?.length) {
    return {
      uuid: answer.uuid,
      answerChoice: answer.answerChoice,
    }
  }
}

interface UseAnswersParams {
  evaluation?: ReviewAnswerEvaluationFragment | null
  questionnaireContent: PerformanceQuestionnaireAnsweredElement[]
  employeeScoreQuestionnaireContent: PerformanceQuestionnaireAnsweredElement[]
  employeePotentialScoreQuestionnaireContent: PerformanceQuestionnaireAnsweredElement[]
  competenciesQuestionnaireContent: PerformanceQuestionnaireAnsweredElement[]
  autosave?: boolean
  onAnswer?: (reviewId: number) => void
  onDraft?: (reviewId: number) => void
}

export const useAnswers = ({
  evaluation,
  questionnaireContent,
  employeeScoreQuestionnaireContent,
  employeePotentialScoreQuestionnaireContent,
  competenciesQuestionnaireContent,
  autosave = true,
  onAnswer,
  onDraft,
}: UseAnswersParams) => {
  const questionnaireSize =
    questionnaireContent.length +
    employeeScoreQuestionnaireContent.length +
    employeePotentialScoreQuestionnaireContent.length

  const initialAnswers = [
    ...questionnaireContent,
    ...competenciesQuestionnaireContent,
    ...employeeScoreQuestionnaireContent,
    ...employeePotentialScoreQuestionnaireContent,
  ].flatMap(({ questions }) =>
    questions.map(({ question, answer }) => ({
      uuid: question.uuid,
      answerText: answer?.answerText ?? null,
      answerRating: answer?.answerRating ?? null,
      answerChoice: answer?.answerChoice ?? null,
      answerInt: answer?.answerInt ?? null,
    }))
  )

  const [answers, setAnswers] = useState<PerformanceAnswer[]>(initialAnswers)
  const debouncedAnswers = useDebounce(answers, 1000)

  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const [draftDirty, setDraftDirty] = useState(false)
  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null)

  const prepareAnswersToSubmit = (allAnswers: PerformanceAnswer[]) => {
    const answers = allAnswers
      .filter((answer) => answer.uuid)
      .map(prepareAnswerInput)
      .filter(Boolean)

    const [employeeScoreAnswer] = remove(
      answers,
      (answer) => answer.uuid === employeeScoreQuestionnaireContent[0]?.questions[0]?.question.uuid
    )

    const [employeePotentialScoreAnswer] = remove(
      answers,
      (answer) =>
        answer.uuid === employeePotentialScoreQuestionnaireContent[0]?.questions[0]?.question.uuid
    )

    return { answers, employeeScoreAnswer, employeePotentialScoreAnswer }
  }

  const [draftEvaluationMutation, { loading: submittingDraft }] = useMutation(
    DraftPerformanceReviewEvaluationAnswerDocument,
    {
      extractorFn: (data) => data.performanceMutations.draftPerformanceReviewEvaluationAnswer,
    }
  )

  const [publishEvaluationMutation, { loading: submittingAnswer }] = useMutation(
    PublishPerformanceReviewEvaluationAnswerDocument,
    {
      extractorFn: (data) => data.performanceMutations.publishPerformanceReviewEvaluationAnswer,
    }
  )

  const [republishEvaluationMutation, { loading: submittingRepublish }] = useMutation(
    RepublishPerformanceReviewEvaluationAnswerDocument,
    {
      extractorFn: (data) => data.performanceMutations.republishPerformanceReviewEvaluationAnswer,
    }
  )

  const updateAnswer = (updatedAnswer: PerformanceAnswer) => {
    const updatedAnswers = answers.map((answer) =>
      answer.uuid === updatedAnswer.uuid ? updatedAnswer : answer
    )

    if (!updatedAnswers.some((answer) => answer.uuid === updatedAnswer.uuid)) {
      updatedAnswers.push(updatedAnswer)
    }

    setDraftDirty(true)
    setAnswers(updatedAnswers)
  }

  const submitDraftAnswer = useUnsafeCallback(
    async (answers: PerformanceAnswer[]) => {
      const updatedAnswers = answers.filter(
        (answer) =>
          answer.answerText !== null ||
          answer.answerRating !== null ||
          answer.answerChoice !== null ||
          answer.answerInt !== null
      )

      const answersToSubmit = prepareAnswersToSubmit(updatedAnswers)

      if (!evaluation?.id) {
        throw 'No evaluation id sent'
      }

      draftEvaluationMutation({
        variables: {
          performanceReviewEvaluationId: Number(evaluation.id),
          ...answersToSubmit,
        },
        onCompleted: (data) => {
          const errors = data.performanceMutations.draftPerformanceReviewEvaluationAnswer.errors

          const reviewId =
            data.performanceMutations.draftPerformanceReviewEvaluationAnswer?.reviewEvaluationAnswer
              ?.evaluation?.performanceReviewProcess?.id

          if (errors.length) {
            const errorMessages = errors.map((error) => {
              if (error.__typename === 'StructuredError') return error.messages.join('\n')
              if (error.__typename === 'SimpleError') return error.message
            })
            app.setFlash({
              type: 'wrong',
              message: errorMessages.join('\n'),
            })
          }

          if (reviewId) {
            onDraft?.(reviewId)
          }

          setDraftDirty(false)
          setDraftSavedAt(new Date())
        },
      })
    },
    [draftEvaluationMutation, evaluation?.id, onDraft]
  )

  const prepareSubmitAnswers = () => {
    const updatedAnswers = answers.filter(
      (answer) =>
        answer.answerText !== null ||
        answer.answerRating !== null ||
        answer.answerChoice !== null ||
        answer.answerInt !== null
    )
    return prepareAnswersToSubmit(updatedAnswers)
  }

  const handleSuccess = (successMessage: string, isRepublish = false) => {
    app.setFlash({
      type: 'correct',
      message: successMessage,
    })
    setHasAttemptedSubmit(false)
    track(trackingEvents.performanceProcess.completeEvaluationSubmitted, {
      reviewer_strategy: evaluation?.evaluationType,
      end_date: i18nDateToString(new Date(), 'backend'),
      ...(isRepublish && { is_republish: true }),
    })
  }

  const submitPublishAnswer = async () => {
    setHasAttemptedSubmit(true)

    const answersToSubmit = prepareSubmitAnswers()

    if (!evaluation?.id) {
      throw 'No evaluation id sent'
    }

    const response = await publishEvaluationMutation({
      variables: {
        performanceReviewEvaluationId: Number(evaluation.id),
        ...answersToSubmit,
      },
    })

    const result = handleResult(response, () => {})

    if (result.success) {
      handleSuccess(i18n.t('common.saved_successfully'))
      const reviewId = result.data.reviewEvaluationAnswer?.evaluation?.performanceReviewProcess?.id
      if (reviewId && onAnswer) {
        onAnswer(reviewId)
      }
    }
  }

  const submitRepublishAnswer = async () => {
    setHasAttemptedSubmit(true)

    const answersToSubmit = prepareSubmitAnswers()

    if (!evaluation?.id) {
      throw 'No evaluation id sent'
    }

    const response = await republishEvaluationMutation({
      variables: {
        performanceReviewEvaluationId: Number(evaluation.id),
        ...answersToSubmit,
      },
    })

    const result = handleResult(response, () => {})

    if (result.success) {
      handleSuccess(i18n.t('common.saved_successfully'), true)
      const reviewId = result.data.reviewEvaluationAnswer?.evaluation?.performanceReviewProcess?.id
      if (reviewId && onAnswer) {
        onAnswer(reviewId)
      }
    }
  }

  useUnsafeEffect(() => {
    if (!autosave) return
    if (draftDirty) submitDraftAnswer(debouncedAnswers)
  }, [submitDraftAnswer, debouncedAnswers, autosave])

  useEffect(() => {
    if (!questionnaireSize && questionnaireSize !== 0) return
    setAnswers(initialAnswers)
  }, [questionnaireSize])

  useEffect(() => {
    const canAnswer = evaluation?.answeredQuestionnaires?._policies?.answer?.can

    if (questionnaireSize === 0 || !canAnswer || anyQuestionAnswered(initialAnswers)) return

    track(trackingEvents.performanceProcess.completeEvaluationStarted, {
      reviewer_strategy: evaluation?.evaluationType,
      start_date: i18nDateToString(new Date(), 'backend'),
    })
  }, [questionnaireSize])

  return {
    answers,
    draftDirty,
    draftSavedAt,
    updateAnswer,
    submitDraftAnswer: () => submitDraftAnswer(answers),
    submitPublishAnswer,
    submitRepublishAnswer,
    submittingDraft,
    submittingAnswer,
    submittingRepublish,
    hasAttemptedSubmit,
  }
}
